/**
 * File: apps/qbench-cp/reactapp/src/shared/components/FormV2/Checkbox.js
 *
 * Purpose: Renders the boolean checkbox input used by FormV2 for BOOL field types.
 *
 * Scope: FormV2 input rendering for boolean entity fields.
 *
 * Tags: customer-portal
 *
 * Overview: Exports Checkbox, a memoized component that lays out an Input of type checkbox
 *     next to its Label using a horizontal Stack, applying read-only styling/attributes when
 *     the field is read-only. The attached `transform` strategy coerces server values to a
 *     boolean via toBoolean for both fromServer and defaultValue, passing toServer through
 *     unchanged.
 *
 * Exports: default Checkbox component (with an attached `transform` static property).
 */

import React from 'react';
import classNames from 'classnames';
import { Stack } from 'react-bootstrap';
import { toBoolean } from 'shared/utils/functions';
import Input, { readOnlyAttrs } from './Input';
import Label from './Label';

/**
 * @type {import('./types').FieldTransformStrategy}
 */
const transform = {
  fromServer(value) {
    return toBoolean(value);
  },
  toServer(value) {
    const payload = value;
    return payload;
  },
  defaultValue(value) {
    return toBoolean(value);
  },
};

const Checkbox = React.memo(function Checkbox(props) {
  const { name, displayName, required = false, readOnly } = props;
  return (
    <Stack direction="horizontal" className="mb-3">
      <Input
        type="checkbox"
        id={name}
        className={classNames(
          'me-2',
          readOnly && 'bg-light text-muted cursor-not-allowed',
        )}
        {...(readOnly && readOnlyAttrs)}
        {...props}
      />
      <Label htmlFor={name} required={required} readOnly={readOnly}>
        {displayName}
      </Label>
    </Stack>
  );
});

Checkbox.transform = transform;
export default Checkbox;
